import { useEffect, useMemo, useState } from "react";
import { Modal, TreeSelect } from "antd";
import { folderNodeKey } from "./personalTreeUtils";

// Builds the antd <TreeSelect> `treeData` shape (value/title/children) from
// the same flat folder list the file browser uses, so a document can be
// dropped anywhere in the user's own folder tree — including subfolders
// nested arbitrarily deep, per the plan's "folders and subfolders" ask.
const buildDestinationOptions = (rootId, folders) => {
  const byParent = new Map();
  for (const folder of folders) {
    const parentKey = folder.parent_id || "root";
    if (!byParent.has(parentKey)) byParent.set(parentKey, []);
    byParent.get(parentKey).push(folder);
  }
  const build = (folderId) =>
    (byParent.get(folderId) || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((folder) => ({
        value: folder.id,
        title: folder.name,
        children: build(folder.id),
      }));

  const root = folders.find((folder) => folder.id === rootId);
  return [
    {
      value: rootId,
      title: root?.name || "My Files",
      children: build(rootId),
    },
  ];
};

export default function MoveDocumentModal({
  open,
  document,
  rootId,
  folders,
  onClose,
  onMove,
}) {
  const [destination, setDestination] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setDestination(document?.folder_id || rootId || null);
      setError("");
      setSaving(false);
    }
  }, [open, document, rootId]);

  const treeData = useMemo(
    () => buildDestinationOptions(rootId, folders),
    [rootId, folders]
  );

  const handleOk = async () => {
    if (!destination) {
      setError("Pick a destination folder.");
      return;
    }
    if (destination === document?.folder_id) {
      onClose();
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onMove(destination);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error || "Could not move the file."
      );
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Move "${document?.name || ""}"`}
      onCancel={saving ? undefined : onClose}
      onOk={handleOk}
      okText="Move"
      confirmLoading={saving}
      destroyOnHidden
    >
      <TreeSelect
        style={{ width: "100%" }}
        value={destination}
        treeData={treeData}
        fieldNames={{ label: "title", value: "value", children: "children" }}
        treeDefaultExpandAll
        onChange={setDestination}
        placeholder="Choose a destination folder"
      />
      {error && (
        <div role="alert" style={{ marginTop: 10, color: "#b42318", fontSize: 13 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}

export { buildDestinationOptions, folderNodeKey };
